import React, { FC } from 'react'
import { GlobalStyle } from '@moonpig/launchpad-components'
import { GraphQLContext } from '@moonpig/web-core-graphql'
import { Region } from '@moonpig/web-core-types'
import { useFlag } from '@moonpig/web-core-eppo/browser'
import { EppoConfiguration } from '@moonpig/web-core-eppo/types'
import { GetExperiment } from '@moonpig/web-core-eppo/server'
import { useLocaleText } from './AppShell.locale'
import { AppBanner } from './components/AppBanner'
import { OneTrustCookieBanner } from './components/CookieBanner'
import {
  AppBannerInitialProps,
  getAppBannerInitialProps,
} from './data/appBanner'
import { getLayoutInitialProps, LayoutInitialProps } from './data/layout'
import { Layout } from './Layout'
import { HideSearchBarOption } from './components/DefaultLayout'
import { getGlobalStyleEnvironment } from '@moonpig/web-core-utils/src/runtimeEnv/runtimeEnv'

type LayoutType = 'none' | 'default' | 'checkout' | 'auth' | 'attach'

export type AppShellContext = {
  environmentName: string
  graphQL: { query: GraphQLContext['query'] }
  layoutType: LayoutType
  path: string
  preview: boolean
  query: { [key: string]: string | string[] | undefined }
  region: Region
  shouldHideFooter?: boolean
  hideSearchBar?: HideSearchBarOption
  hideGoogleOneTap?: boolean
  shouldRenderAppBanner: boolean
  eppoConfiguration: EppoConfiguration
  getExperiment: GetExperiment
}

export type AppShellProps = {
  appBanner: AppBannerInitialProps | null
  environmentName: string
  layout: LayoutInitialProps
  path: string
}

type AppShellFC = FC<React.PropsWithChildren<AppShellProps>> & {
  getInitialProps: (context: AppShellContext) => Promise<AppShellProps>
}

export const AppShell: AppShellFC = ({
  appBanner,
  children,
  environmentName,
  layout,
  path,
}) => {
  const t = useLocaleText()

  const disableCookieBanner = useFlag('disable-cookie-banner', {
    fallback: false,
  })
  const env = getGlobalStyleEnvironment()

  return (
    <>
      <GlobalStyle env={env} />
      {!disableCookieBanner ? (
        <OneTrustCookieBanner environmentName={environmentName} />
      ) : null}
      {appBanner && (
        <AppBanner
          title={appBanner.title}
          subtitle={appBanner.subtitle}
          ctaText="Get"
          url={appBanner.url}
          voucherCode={appBanner.voucherCode}
          shouldFormat={appBanner.shouldFormat}
          totalRatingsText={appBanner.totalRatings}
          rating={appBanner.rating}
          dismissTitle={t('close_the_smart_banner')}
          path={path}
        />
      )}
      <Layout {...layout}>{children}</Layout>
    </>
  )
}

AppShell.getInitialProps = async ({
  environmentName,
  graphQL,
  layoutType,
  path,
  preview,
  query,
  region,
  shouldHideFooter,
  hideSearchBar,
  hideGoogleOneTap,
  shouldRenderAppBanner,
  getExperiment,
}) => {
  const appBannerPromise = shouldRenderAppBanner
    ? getAppBannerInitialProps({ graphQL, preview })
    : null

  const layoutPromise = getLayoutInitialProps({
    graphQL,
    layoutType,
    path,
    preview,
    query,
    region,
    shouldHideFooter,
    hideSearchBar,
    hideGoogleOneTap,
    getExperiment,
  })

  const [appBanner, layout] = await Promise.all([
    appBannerPromise,
    layoutPromise,
  ])

  const initialProps: AppShellProps = {
    appBanner,
    environmentName,
    layout,
    path,
  }

  return initialProps
}
